import { useCallback, useEffect, useMemo, useState } from "react";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import { SearchBox } from "../../../components/Search";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { BrushStart } from "../../../models/chart_model";
import { ChhattisgarhDCNetScheduleItem } from "../../../models/rajasthan";
import { renderHourTick } from "../../../components/recharts/components";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import { useSelector } from "react-redux";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { AddDatesWithFileName } from "../../../helpers/helper";
import { H_CHART_TYPES } from "../../Hppc/const";
import { formatDateForAPI } from "../const";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import MetricChip from "../../../components/Metrics";
import { getChhattisgarhDCNetScheduleData } from "../Utility/RestApi";
import { DiscomLoadMetrics } from "../../../models/metrics";
import { ReactComponent as PeakIcon } from "../../../icons/peakicon.svg";
import { ReactComponent as AvgIcon } from "../../../icons/AvgIcon.svg";
import { ReactComponent as MinIcon } from "../../../icons/MinIcon.svg";
import { ReactComponent as TotalIcon } from "../../../icons/TotalIcon.svg";
import { normalizeDisplayLabel } from "../../../utility/displayText";
let Intervals = [
  {
    label: "Time Slot",
    key: "slot",
    values: ["block", "date"],
    isTime: true,
  },
  {
    label: "Date",
    key: "date",
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    values: ["month"],
    isTime: false,
  },
];
const UNITS = ["MW", "MWh"];
const TYPE_OPTIONS_BASE = [
  { label: "DC", suffixBase: "dc", displayName: "DC" },
  { label: "Actual", suffixBase: "actual", displayName: "Actual" },
  { label: "Deviation", suffixBase: "diff", displayName: "Deviation" },
];

// Helper function to get month with year (e.g., "Oct-24" instead of just "Oct")
export function getMonthWithYear(dateString: string): string {
  const date = new Date(dateString);
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear().toString().slice(-2);
  return `${month}-${year}`;
}

export function ChhattisgarhDCNetSchedule() {
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );

  const [data, setData] = useState<ChhattisgarhDCNetScheduleItem[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [plantNames, setPlantNames] = useState<string[]>([]);
  const [selectedPlants, setSelectedPlants] = useState<string[]>([]);
  const [selectedUnit, setSelectedUnit] = useState(UNITS[0]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>(["Deviation"]);
  const [selectedPowerTypes, setSelectedPowerTypes] = useState<string[]>([
    "Green Power",
    "Grey Power",
  ]);
  const [traderNames, setTraderNames] = useState<string[]>([]);
  const [selectedTraders, setSelectedTraders] = useState<string[]>([]);
  const [uniqueDate, setUniqueDate] = useState<any[]>([]);
  const [selectedDate, setSelectedDate] = useState<any[]>([]);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [TableData, setTableData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[1]);
  const [selectedInterval, setSelectedInterval] = useState(Intervals[0]);
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<DiscomLoadMetrics | null>(null);

  const TYPE_OPTIONS = useMemo(
    () =>
      TYPE_OPTIONS_BASE.map((t) => ({
        ...t,
        suffix: `${t.suffixBase}_${selectedUnit.toLowerCase()}`,
      })),
    [selectedUnit],
  );

  const selectedTypeSuffixes = useMemo(() => {
    return TYPE_OPTIONS.filter((t) => selectedTypes.includes(t.label));
  }, [selectedTypes, TYPE_OPTIONS]);

  // Inter-filterable: available traders filtered by selected entities (and vice versa)
  const availableTraderNames = useMemo(() => {
    if (selectedPlants.length === 0) return traderNames;
    return Array.from(
      new Set(
        data
          .filter((item) => selectedPlants.includes(item.seller_dc))
          .map((item) => item.trader),
      ),
    ).sort();
  }, [data, selectedPlants, traderNames]);

  const availablePlantNames = useMemo(() => {
    if (selectedTraders.length === 0) return plantNames;
    return Array.from(
      new Set(
        data
          .filter((item) => selectedTraders.includes(item.trader))
          .map((item) => item.seller_dc),
      ),
    ).sort();
  }, [data, selectedTraders, plantNames]);

  const _handlefetchData = useCallback(async () => {
    if (DefaultDates[activeTab].defaultRangeDate === undefined) return;
    setLoading(true);
    const dates = DefaultDates[activeTab].defaultRangeDate;
    const startDateStr = formatDateForAPI(dates.startDate);
    const endDateStr = formatDateForAPI(dates.endDate);
    try {
      const data = await getChhattisgarhDCNetScheduleData(
        startDateStr,
        endDateStr,
      );
      const dataMap = new Map<string, ChhattisgarhDCNetScheduleItem>();
      data.forEach((item) => {
        const uniqueKey = `${item.date}_${item.block}_${item.seller_dc}_${item.trader}`;
        if (!dataMap.has(uniqueKey)) {
          dataMap.set(uniqueKey, item);
        }
      });
      const newData = Array.from(dataMap.values());

      setData(newData);

      // Extract unique generator names and trader names
      const uniquePlants = Array.from(
        new Set(newData.map((item) => item.seller_dc)),
      ).sort();
      setPlantNames(uniquePlants);

      const uniqueTraders = Array.from(
        new Set(newData.map((item) => item.trader)),
      ).sort();
      setTraderNames(uniqueTraders);

      setUniqueDate(
        Array.from(
          new Set(
            newData
              .map((item) => item.date)
              .sort((a, b) => new Date(a).getTime() - new Date(b).getTime()),
          ),
        ),
      );
      setUniqueBlock(Array.from(new Set(newData.map((item) => item.block))));
    } catch (error) {
      console.error("Error in data fetching:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [DefaultDates, activeTab]);

  useEffect(() => {
    let filteredData: any[] = data.map((item) => ({
      ...item,
      month: getMonthWithYear(item.date),
    }));

    if (selectedDate.length > 0) {
      filteredData = filteredData.filter((item: any) =>
        selectedDate.map((d) => d.value).includes(item.date),
      );
    }
    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item: any) =>
        selectedBlock.map((b) => b.value).includes(item.block),
      );
    }
    if (selectedPlants.length > 0) {
      filteredData = filteredData.filter((item: any) =>
        selectedPlants.includes(item.seller_dc),
      );
    }
    if (selectedPowerTypes.length > 0 && selectedPowerTypes.length < 2) {
      const normalize = (s: string) => s.toLowerCase().replace("grey", "gray");
      const lowerSelected = selectedPowerTypes.map(normalize);
      filteredData = filteredData.filter((item: any) =>
        lowerSelected.includes(normalize(item.power_type || "")),
      );
    }
    if (selectedTraders.length > 0) {
      filteredData = filteredData.filter((item: any) =>
        selectedTraders.includes(item.trader),
      );
    }

    // Compute MWh fields (each block = 15 min = 0.25 hr)
    filteredData = filteredData.map((item: any) => ({
      ...item,
      dc_mwh: item.dc_mw * 0.25,
      actual_mwh: item.actual_mw * 0.25,
      diff_mwh: item.diff_mw * 0.25,
    }));

    // Sort filtered data in ascending order by date and block
    filteredData.sort((a: any, b: any) => {
      const dateCompare =
        new Date(a.date).getTime() - new Date(b.date).getTime();
      if (dateCompare !== 0) return dateCompare;
      return a.block - b.block;
    });

    function getKeys() {
      if (selectedInterval.key === "slot") {
        return ["date", "block"];
      } else if (selectedInterval.key === "date") {
        return ["date"];
      } else {
        return [selectedInterval.values[0]];
      }
    }

    // Get the aggregation keys based on selected types
    const aggKeys = selectedTypeSuffixes.map((t) => t.suffix);

    const par: ChartDataParams = {
      keys: getKeys(),
      groupBy: ["seller_dc"],
      tableKeys: ["date", "block", "month"],
      volumeKey: ``,
      aggregationKeys: aggKeys,
      priceKey: "",
      averageKeys: aggKeys,
      averageDuplicationKeys: ["date", "block", "month", "seller_dc"],
      data: filteredData,
    };
    const cData = FormatChartData(par);
    setChartData(cData.data);
    setChartKeys(cData.keys);
    setTableData(cData.tableData || []);
  }, [
    data,
    selectedInterval,
    selectedPlants,
    selectedDate,
    selectedBlock,
    selectedTypeSuffixes,
    selectedPowerTypes,
    selectedTraders,
    selectedUnit,
  ]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  const handlexDataKey = useCallback(() => {
    if (selectedInterval.isTime) {
      if (selectedBlock.length > 0) {
        if (selectedInterval.isTime) {
          return "block";
        } else {
          return selectedInterval.values[0];
        }
      }
      return "block";
    } else {
      return selectedInterval.key;
    }
  }, [
    selectedBlock.length,
    selectedInterval.isTime,
    selectedInterval.key,
    selectedInterval.values,
  ]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);

  function secondDataKey() {
    if (selectedInterval.isTime) {
      if (selectedBlock.length > 0) {
        return "date";
      }
      return "date";
    } else {
      return undefined;
    }
  }

  const summaryExtraKeys = selectedInterval.isTime
    ? ["block", selectedInterval.values[1]]
    : [selectedInterval.values[0]];

  const summaryExtraLabels = selectedInterval.values.map((value) =>
    value === "date" ? "Date" : value,
  );

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(getLegends());

  const tablePar = {
    legends: visibleLegends,
    data: chartData,
    tableData: TableData,
    itemsPerPage: 96,
    block_key: "block",
    unit: selectedUnit,
    extraKeys: summaryExtraKeys,
    extraLabels: summaryExtraLabels,
    filename: AddDatesWithFileName({
      fileName: "CSPDCL DC Net Schedule",
      dates: DefaultDates[activeTab]?.defaultRangeDate || [],
    }),
  };

  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="Intervals"
            items={Intervals.map((item) => item.label)}
            selectedItems={[selectedInterval.label]}
            onSelect={(item, index) => {
              setSelectedInterval(Intervals[index]);
            }}
          />
          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item) => setSelectedUnit(item)}
          />
          <FilterMenu
            title="Type"
            items={TYPE_OPTIONS.map((t) => t.label)}
            selectedItems={selectedTypes}
            setSelectedItems={setSelectedTypes}
          />

          <FilterMenu
            title="Power Type"
            items={["Green Power", "Grey Power"]}
            selectedItems={selectedPowerTypes}
            setSelectedItems={setSelectedPowerTypes}
          />
          <FilterMenu
            title="Chart Type"
            items={H_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) => {
              setSelectedChartType(H_CHART_TYPES[index]);
            }}
          />
        </div>
      </NewFilterContainer>
      <MainPageContainer>
        <SecondaryHeader />
        <div className="gas-chart-container">
          <div className="gas-heading gap-x-10">
            <h2 className="chartHeading w-60">DC vs Actual ({selectedUnit})</h2>
            <div className="flex gap-x-6  justify-end">
              <SearchBox
                options={availablePlantNames.map((p) => ({
                  label: p,
                  value: p,
                }))}
                selectedOptions={selectedPlants.map((p) => ({
                  label: p,
                  value: p,
                }))}
                onChange={(selected) => {
                  setSelectedPlants(
                    (selected || []).map((item: any) => item.value),
                  );
                }}
                placeholder="Search Entity"
                isMany={true}
              />
              <SearchBox
                options={availableTraderNames.map((t) => ({
                  label: t,
                  value: t,
                }))}
                selectedOptions={selectedTraders.map((t) => ({
                  label: t,
                  value: t,
                }))}
                onChange={(selected) => {
                  setSelectedTraders(
                    (selected || []).map((item: any) => item.value),
                  );
                }}
                placeholder="Search Trader"
                isMany={true}
              />
              <BlockRangeSelector
                blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                placeholder="Search Block"
                selectedOptions={selectedBlock.map((item) => item.value)}
                setSelectedOptions={setSelectedBlock}
              />
              <button
                onClick={() => {
                  dataTab.label === "Chart" && downloadChartTableCSV(tablePar);
                  dataTab.label === "Summary" && downloadSummaryCSV(tablePar);
                  dataTab.label === "Table" && downloadChartTableCSV(tablePar);
                }}
                className="downloadButton"
              >
                Download Data
              </button>
            </div>
          </div>
          {loading ? (
            <ChartAreaLoader
              title="Loading DC Net Schedule"
              subtitle="Fetching CSPDCL DC Net Schedule data…"
            />
          ) : (
            <>
              {metrics && (
                <div className="flex flex-wrap gap-4 my-2 justify-center">
                  <MetricChip
                    icon={<PeakIcon width={20} height={19} />}
                    items={[
                      {
                        label: "Peak Load",
                        value: metrics["Peak Load (MW)"].value,
                        unit: metrics["Peak Load (MW)"].unit,
                      },
                    ]}
                  />
                  <MetricChip
                    icon={<AvgIcon width={20} height={19} />}
                    items={[
                      {
                        label: "Average Load",
                        value: metrics["Average Load (MW)"].value,
                        unit: metrics["Average Load (MW)"].unit,
                      },
                    ]}
                  />
                  <MetricChip
                    icon={<MinIcon width={20} height={19} />}
                    items={[
                      {
                        label: "Minimum Load",
                        value: metrics["Minimum Load (MW)"].value,
                        unit: metrics["Minimum Load (MW)"].unit,
                      },
                    ]}
                  />
                  <MetricChip
                    icon={<TotalIcon width={20} height={19} />}
                    items={[
                      {
                        label: "Total Energy",
                        value: metrics["Total Energy (MU)"].value,
                        unit: metrics["Total Energy (MU)"].unit,
                      },
                    ]}
                  />
                </div>
              )}
              {chartData.length === 0 && !loading && (
                <div
                  style={{
                    padding: "40px",
                    textAlign: "center",
                    color: "#666",
                  }}
                >
                  No data available for the selected dates.
                </div>
              )}
              {dataTab.label === "Chart" && (
                <ReMixChart
                  data={chartData}
                  showBrush={chartData.length > 30 ? true : undefined}
                  brushStart={BrushStart.Start}
                  isTimeSlot={selectedInterval.isTime}
                  onlyTitle={true}
                  barCategoryGap={10}
                  unit={selectedUnit}
                  yAxisLabel={selectedUnit}
                  legends={getLegends()}
                  onLegendSelect={handleLegendSelect}
                  defaultSelectedLegends={selectedLegends}
                  xDataKey={xDataKey}
                  xTick={
                    selectedInterval.isTime && selectedBlock.length === 0
                      ? (value: any, index: any) => {
                          return renderHourTick(value, 10);
                        }
                      : undefined
                  }
                  secondXDataKey={secondDataKey()}
                />
              )}
              {dataTab.label === "Summary" && (
                <LegendSummaryTable {...tablePar} />
              )}
              {dataTab.label === "Table" && <ChartTable {...tablePar} />}
            </>
          )}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip
              note={<p>CSPDCL</p>}
              text="
            1. Actual Load data is sourced from CSPDCL's official records."
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );

  function getLegends() {
    // For each selected type suffix (dc_mw, net_schedule_mw, deviation_mw),
    // create a legend entry for each plant (chartKeys = plant names from groupBy)
    return chartKeys.flatMap((key) =>
      selectedTypeSuffixes.map(({ suffix, displayName }) => ({
        dataKey: `${key}_${suffix}`,
        name: `(${normalizeDisplayLabel(key)}) ${displayName}`,
        legendColor: undefined,
        type: selectedChartType.value,
        stackId:
          (selectedChartType as any).stackId !== undefined ? suffix : undefined,
      })),
    );
  }
}
